import { Flashbar, FlashbarProps } from '@amzn/awsui-components-core';

export type FlashbarMessage = FlashbarProps.MessageDefinition & { timeout: number };
export const DEFAULT_FLASHBAR_TIMEOUT = 6000;
class FlashbarMessageBroker {
  public static create() {
    if (!this.instance) {
      this.instance = new FlashbarMessageBroker();
    }
    return this.instance;
  }
  private static instance: FlashbarMessageBroker;

  private subscriptions: Set<(message: FlashbarMessage) => void> = new Set();

  public pushSuccessMessage(
    content: string,
    timeout: number = DEFAULT_FLASHBAR_TIMEOUT,
    header?: string,
  ) {
    const message = {
      content,
      dismissible: true,
      header,
      timeout,
      type: 'success' as FlashbarProps.Type,
    };
    this.pushMessage(message);
    return message;
  }

  /**
   * AVOID USING IN NEW CODE.
   *
   * If you're reaching for flashbar, you probably haven't thought hard enough
   * about where the error actually belongs.
   *
   * The flashbar is poor UX — a global banner detached from the triggering
   * action, easy to miss, and hard to test. It is almost never the right
   * choice for showing an error.
   *
   * For errors, prefer one of these, in order:
   *   1. Inline error — highlight the offending input field and show an
   *      error message next to it. Best for validation and field-level
   *      failures.
   *   2. Alert component rendered next to the action — keeps the error
   *      attached to what the user just did. Ask the UX team for the right
   *      pattern for your case.
   *   3. Full-page error — for fatal errors that prevent the user from
   *      continuing on this page (e.g. permission denied, content not
   *      found).
   *   4. This flash bar.
   *
   * If you genuinely need a flashbar, discuss with the UX team and design
   * a better-looking, better-placed replacement rather than reusing this.
   */
  public pushErrorMessage(
    content: string,
    timeout?: number,
    header?: string,
    buttonText?: string,
    onButtonClick?: () => void,
  ) {
    const message = {
      content,
      dismissible: true,
      header,
      timeout,
      type: 'error' as FlashbarProps.Type,
      buttonText,
      onButtonClick,
    };
    // @ts-expect-error TS2345 - strict migration
    this.pushMessage(message);
    return message;
  }

  public pushWarnMessage(
    content: string,
    timeout: number = DEFAULT_FLASHBAR_TIMEOUT,
    header?: string,
  ) {
    const message = {
      content,
      dismissible: true,
      header,
      timeout,
      type: 'warning' as FlashbarProps.Type,
    };
    this.pushMessage(message);
    return message;
  }

  public pushInfoMessage(
    content: string,
    timeout: number = DEFAULT_FLASHBAR_TIMEOUT,
    header?: string,
  ) {
    const message = {
      content,
      dismissible: true,
      header,
      timeout,
      type: 'info' as FlashbarProps.Type,
    };
    this.pushMessage(message);
    return message;
  }

  public subscribe(handler: (message: FlashbarMessage) => void) {
    this.subscriptions.add(handler);
  }

  public unsubscribe(handler: (message: FlashbarMessage) => void) {
    this.subscriptions.delete(handler);
  }

  private pushMessage(msg: FlashbarMessage) {
    for (const sub of Array.from(this.subscriptions.values())) {
      sub(msg);
    }
  }
}

export default FlashbarMessageBroker;
